import { ChevronDown, Edit05, Expand01, Minimize01, Share06, XClose } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import checkCircle from 'src/assets/icons/filled-icons/check-circle.svg';
import ButtonCmp from 'src/components/ButtonCmp';
import type { RootState } from 'src/redux/store';

import AddressSelectionCard from './AddressSelectionCard';
import AgentComposer from './AgentComposer';
import { clearPendingAgentMessage, closeAgentConversationPanel, setAgentPanelDragging } from './agentConversationPanel.slice';
import AgentEmptyState from './AgentEmptyState';
import AgentMessageBubble, { type AgentMessage } from './AgentMessageBubble';
import AiThinking from './AiThinking';
import AllRatesLists, { type IAllRatesItem } from './AllRatesLists';
import type { Step } from './CommonComponents/AiWorkflow';
import ConversationHistoryModal from './ConversationHistoryModal';
import ConversationLoading from './ConversationLoading';
import EquipmentTypeCard from './EquipmentTypeCard';
import NewDomain from './NewDomain';
import PackagingDetailsCard, { type IPackagingLineItem } from './PackagingDetailsCard';
import PickupDateCard from './PickupDateCard';
import ServiceLevelSelectionCard, { type ShipmentServiceLevel } from './ServiceLevelSelectionCard';
import ServiceTypeSelectionCard, { type ShipmentServiceType } from './ServiceTypeSelectionCard';
import TotalWeightCard from './TotalWeightCard';

const STORAGE_KEY = 'agent_panel_width';
const DEFAULT_WIDTH = 402;
const MIN_WIDTH = 320;
const MAX_WIDTH = 700;

const getStoredWidth = (): number => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);

        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) return parsed;
        }
    } catch {
        // localStorage unavailable
    }

    return DEFAULT_WIDTH;
};

const MOCK_RATES: IAllRatesItem[] = [
    { label: 'XPO Logistics', icon: 'verified', value: '$1,232', status: 'completed', actionType: 'outboundEmail' },
    { label: 'Maritime Ontario', icon: 'verified', value: '$1,067', status: 'completed', actionType: 'outboundCall' },
    { label: 'GOODDAY FREIGHT INC', value: '$1,730', status: 'completed', actionType: 'outboundEmail' },
    { label: 'ABF Freight', icon: 'verified', status: 'loading', actionType: 'outboundEmail' },
    { label: 'Northstar Transit', value: '$1,234', status: 'completed', actionType: 'outboundEmail' },
];

const ORIGIN_SAVED_ADDRESSES = ['2200 Freightliner Dr, Brampton, ON L6S 6X4, Canada', '4501 Steeles Ave W, Vaughan, ON L4L 4A1, Canada', '50 West Wilmot St, Richmond Hill, ON L4B 1M5, Canada'];

const DESTINATION_SAVED_ADDRESSES = ['4000 Saint-Patrick St, Montreal, QC H4E 1A1, Canada', '3000 Rue Joseph-Dubreuil, Lachine, QC H8T 3H6, Canada', '5555 Rue Ferrier, Montreal, QC H4P 1M8, Canada'];

const UNIT_TYPE_LABELS: Record<string, { singular: string; plural: string }> = {
    pallet: { singular: 'Pallet', plural: 'Pallets' },
    box: { singular: 'Box', plural: 'Boxes' },
    crate: { singular: 'Crate', plural: 'Crates' },
    drum: { singular: 'Drum', plural: 'Drums' },
    bundle: { singular: 'Bundle', plural: 'Bundles' },
};

const pluralizeUnitType = (unitType: string, count: string): string => {
    const labels = UNIT_TYPE_LABELS[unitType] ?? { singular: unitType, plural: unitType };

    return Number(count) === 1 ? labels.singular : labels.plural;
};

const SERVICE_LEVEL_LABELS: Record<ShipmentServiceLevel, string> = {
    standard: 'Standard',
    expedited: 'Expedited',
    express: 'Express',
};

const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
    dry_van: 'Dry Van',
    reefer: 'Reefer',
    flatbed: 'Flatbed',
    step_deck: 'Step Deck',
};

// The Origin/Destination/Dimensions/Load Details steps accordion — adding a new one collapses
// every earlier step in this group so only the current step stays expanded (Classified the
// request is intentionally excluded; it stays expanded throughout).
const SHIPMENT_STEPS_GROUP = 'shipmentSteps';

const formatItemsSummary = (lineItems: IPackagingLineItem[]): string => {
    const totalWeight = lineItems.reduce((sum, item) => sum + (Number(item.totalWeight) || 0), 0);

    const uniqueUnitTypes = Array.from(new Set(lineItems.map((item) => item.unitType)));
    const itemsDescription =
        uniqueUnitTypes.length === 1
            ? `${lineItems.reduce((sum, item) => sum + (Number(item.noOfHU) || 0), 0)} ${pluralizeUnitType(uniqueUnitTypes[0], '2').toLowerCase()}`
            : lineItems.map((item) => `${item.noOfHU} ${pluralizeUnitType(item.unitType, item.noOfHU).toLowerCase()}`).join(', ');

    return `${itemsDescription} - ${totalWeight.toLocaleString('en-US')} lbs`;
};

const CLASSIFIED_REQUEST_STEPS: Step[] = [
    {
        title: 'Classified the request · Shipment · LTL',
        items: [
            { label: 'Task', value: 'Shipment Creation Request' },
            { label: 'Type', value: 'Not Found' },
        ],
    },
];

const AgentConversationPanel = () => {
    const dispatch = useDispatch();
    const isOpen = useSelector((state: RootState) => (state as any).agentConversationPanel?.isOpen ?? false);
    const pendingMessage = useSelector((state: RootState) => (state as any).agentConversationPanel?.pendingMessage ?? null);
    const currentUser = useSelector((state: RootState) => state.auth.currentUser);
    // Feature gate: the entry points (TopHeader orb, AskDashboardComposer) check this flag, but the
    // panel itself must too — auth.portalVisibility is redux-persisted, so a stale `true` from a
    // previous session/customer can open the panel before fresh getUserDetails data flips it false.
    const isAgentViewVisible = useSelector((state: RootState) => (state.auth as any).portalVisibility?.enableAgentView === true);

    const [messages, setMessages] = useState<AgentMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [conversationHistoryOpen, setConversationHistoryOpen] = useState(false);
    const [panelWidth, setPanelWidth] = useState(getStoredWidth);
    const [isDragging, setIsDragging] = useState(false);
    const [taskTitle, setTaskTitle] = useState<string | null>(null);
    const [showServiceTypeSelect, setShowServiceTypeSelect] = useState(false);
    const [showOriginAddressSelect, setShowOriginAddressSelect] = useState(false);
    const [showDestinationAddressSelect, setShowDestinationAddressSelect] = useState(false);
    const [showPackagingDetails, setShowPackagingDetails] = useState(false);
    const [showServiceLevelSelect, setShowServiceLevelSelect] = useState(false);
    const [showTotalWeightSelect, setShowTotalWeightSelect] = useState(false);
    const [showPickupDateSelect, setShowPickupDateSelect] = useState(false);
    const [showEquipmentTypeSelect, setShowEquipmentTypeSelect] = useState(false);
    const [showNewDomainModal, setShowNewDomainModal] = useState(false);
    const thinkingMessageIdRef = useRef<string | null>(null);
    const classifyMessageIdRef = useRef<string | null>(null);
    const shipmentAgentMessageIdRef = useRef<string | null>(null);
    const serviceTypeRef = useRef<ShipmentServiceType | null>(null);
    const packagingLineItemsRef = useRef<IPackagingLineItem[]>([]);
    const wizardDataRef = useRef<{ serviceLevel?: ShipmentServiceLevel; totalWeight?: string; linearFootage?: string; pickupDate?: Date; equipmentType?: string }>({});
    // Set while we've asked for an origin/destination address and are waiting for the user to
    // type a reply in the composer — the address-select modal only opens once they respond.
    const pendingAddressStepRef = useRef<'origin' | 'destination' | null>(null);
    const dragStateRef = useRef({ startX: 0, startWidth: 0 });
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    // handleSend is defined further down; this ref lets the earlier-declared skeleton effect call
    // the latest version without a use-before-define, and without dragging its whole definition up.
    const handleSendRef = useRef<(text: string) => void>();

    // Pushes a new step in the Origin/Destination/Dimensions/Load Details accordion, collapsing
    // every earlier step in that same group so only the newest one stays expanded.
    const pushShipmentStep = (id: string, steps: Step[]) => {
        setMessages((prev) => [
            ...prev.map((m) => (m.content?.type === 'workflow' && m.content.group === SHIPMENT_STEPS_GROUP ? { ...m, content: { ...m.content, defaultExpanded: false } } : m)),
            { id, sender: 'agent', content: { type: 'workflow', steps, group: SHIPMENT_STEPS_GROUP } },
        ]);
    };

    useEffect(() => {
        document.documentElement.style.setProperty('--agent-panel-width', `${panelWidth}px`);
    }, [panelWidth]);

    // Drag-to-resize
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragStateRef.current = { startX: e.clientX, startWidth: panelWidth };
            setIsDragging(true);
            dispatch(setAgentPanelDragging(true));
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [panelWidth, dispatch]
    );

    const handleResizeKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const delta = e.key === 'ArrowLeft' ? 16 : -16;
        setPanelWidth((w) => Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w + delta)));
    }, []);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            const { startX, startWidth } = dragStateRef.current;
            const delta = startX - e.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
            setPanelWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            dispatch(setAgentPanelDragging(false));
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dispatch]);

    // Persist width
    useEffect(() => {
        if (!isDragging && panelWidth !== DEFAULT_WIDTH) {
            try {
                localStorage.setItem(STORAGE_KEY, String(panelWidth));
            } catch {
                // localStorage unavailable
            }
        }
    }, [isDragging, panelWidth]);

    const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
        const el = scrollContainerRef.current;
        el?.scrollTo?.({ top: el.scrollHeight, behavior });
    };

    // Show skeleton on first open, then send any pending message (from e.g. the Dashboard
    // composer) once it's done — `justOpened` is computed synchronously from the ref comparison
    // rather than reading `isInitialLoading` state, since a same-commit setIsInitialLoading(true)
    // wouldn't be visible yet to a naive state read here, letting a pending message slip through
    // and open its modal while the skeleton is still (about to be) shown underneath.
    const prevOpenRef = useRef(isOpen);
    useEffect(() => {
        const justOpened = !prevOpenRef.current && isOpen;
        prevOpenRef.current = isOpen;

        if (justOpened && messages.length === 0) {
            setIsInitialLoading(true);
            const id = window.setTimeout(() => {
                setIsInitialLoading(false);

                if (pendingMessage) {
                    handleSendRef.current?.(pendingMessage);
                    dispatch(clearPendingAgentMessage());
                }
            }, 1200);

            return () => window.clearTimeout(id);
        }

        // Not a fresh open (panel was already open/loaded) — send any pending message right away.
        if (!justOpened && pendingMessage && isOpen) {
            handleSendRef.current?.(pendingMessage);
            dispatch(clearPendingAgentMessage());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, messages.length, pendingMessage]);

    useEffect(() => {
        if (!isInitialLoading && scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
            requestAnimationFrame(() => scrollToBottom());
        }
    }, [isInitialLoading]);

    useEffect(() => {
        scrollToBottom();
    }, [messages.length, isLoading]);

    const handleClose = () => {
        setIsFullscreen(false);
        setIsInitialLoading(false);
        dispatch(closeAgentConversationPanel());
    };

    // Step 1 of the "create a shipment" flow: classify the request, then — since the service
    // type (LTL/FTL) can't be determined from the message yet — prompt the user to pick one
    // via ServiceTypeSelectionCard. isLoading is left false throughout; the persistent
    // "Thinking..." taskHeader message (with its own live elapsed timer) is the status
    // indicator for this flow instead of the generic AiThinking dots.
    const runCreateShipmentFlow = () => {
        const thinkingStartedAt = new Date().toISOString();
        const thinkingId = `a-${Date.now()}-thinking`;
        thinkingMessageIdRef.current = thinkingId;

        setMessages((prev) => [
            ...prev,
            {
                id: thinkingId,
                sender: 'agent',
                content: { type: 'taskHeader', title: 'Thinking...', status: 'working', agent: 'kate', startDate: thinkingStartedAt },
            },
        ]);

        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: `a-${Date.now()}-classify-1`,
                    sender: 'agent',
                    text: "Got it — you want to create a shipment. I'll hand this over to your Shipment Agent and keep an eye on it for you.",
                },
            ]);
        }, 800);

        window.setTimeout(() => {
            const classifyId = `a-${Date.now()}-classify-2`;
            classifyMessageIdRef.current = classifyId;

            setMessages((prev) => [
                ...prev,
                {
                    id: classifyId,
                    sender: 'agent',
                    content: { type: 'workflow', steps: CLASSIFIED_REQUEST_STEPS },
                },
            ]);
            setTaskTitle('New Shipment Request');
            setShowServiceTypeSelect(true);
        }, 1800);
    };

    const handleOriginAddressSubmit = (address: string) => {
        setShowOriginAddressSelect(false);
        pushShipmentStep(`a-${Date.now()}-origin-location`, [{ title: 'Origin location', isValuePrice: true, items: [{ value: address }] }]);

        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: `a-${Date.now()}-shipment-agent-ask-destination`,
                    sender: 'agent',
                    text: 'Great! Where is this shipment going? Try a saved location like "Montreal QC".',
                },
            ]);
            pendingAddressStepRef.current = 'destination';
        }, 800);
    };

    const handleDestinationAddressSubmit = (address: string) => {
        setShowDestinationAddressSelect(false);
        pushShipmentStep(`a-${Date.now()}-destination-location`, [{ title: 'Destination location', isValuePrice: true, items: [{ value: address }] }]);

        // FTL shipments don't need per-unit packaging details — skip straight to service level.
        if (serviceTypeRef.current === 'FTL') {
            window.setTimeout(() => setShowServiceLevelSelect(true), 800);

            return;
        }

        window.setTimeout(() => setShowPackagingDetails(true), 800);
    };

    const handlePackagingConfirm = (lineItems: IPackagingLineItem[]) => {
        setShowPackagingDetails(false);
        packagingLineItemsRef.current = lineItems;

        pushShipmentStep(`a-${Date.now()}-packaging-details`, [
            {
                title: 'Dimensions',
                items: lineItems.map((item, index) => ({
                    label: `Unit ${index + 1}`,
                    value: `${item.noOfHU} ${pluralizeUnitType(item.unitType, item.noOfHU)} • ${item.length} × ${item.width} × ${item.height} in`,
                })),
            },
        ]);

        setShowServiceLevelSelect(true);
    };

    const handlePackagingBack = () => {
        setShowPackagingDetails(false);
        setShowDestinationAddressSelect(true);
    };

    const handleServiceLevelNext = (level: ShipmentServiceLevel) => {
        wizardDataRef.current.serviceLevel = level;
        setShowServiceLevelSelect(false);
        setShowTotalWeightSelect(true);
    };

    const handleServiceLevelBack = () => {
        setShowServiceLevelSelect(false);

        if (serviceTypeRef.current === 'FTL') {
            setShowDestinationAddressSelect(true);
        } else {
            setShowPackagingDetails(true);
        }
    };

    const handleTotalWeightNext = (weight: string, linearFootage: string) => {
        wizardDataRef.current.totalWeight = weight;
        wizardDataRef.current.linearFootage = linearFootage;
        setShowTotalWeightSelect(false);
        setShowPickupDateSelect(true);
    };

    const handleTotalWeightBack = () => {
        setShowTotalWeightSelect(false);
        setShowServiceLevelSelect(true);
    };

    const handlePickupDateNext = (date: Date) => {
        wizardDataRef.current.pickupDate = date;
        setShowPickupDateSelect(false);
        setShowEquipmentTypeSelect(true);
    };

    const handlePickupDateBack = () => {
        setShowPickupDateSelect(false);
        setShowTotalWeightSelect(true);
    };

    const handleMakeChanges = () => {
        setShowServiceLevelSelect(true);
    };

    const handleCreateShipment = () => {
        const shipmentNumber = 'TE-2026-000142';

        setMessages((prev) => [
            ...prev,
            {
                id: `a-${Date.now()}-shipment-created`,
                sender: 'agent',
                content: { type: 'notification', text: `Shipment Created ${shipmentNumber}`, icon: <img src={checkCircle} alt="" className="w-3.5 h-3.5" /> },
            },
        ]);

        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: `a-${Date.now()}-shipment-created-followup`,
                    sender: 'agent',
                    content: {
                        type: 'linkMessage',
                        text: "All set! Everything is ready for quoting. Is there anything else you'd like me to do?",
                        linkText: `View Shipment ${shipmentNumber}`,
                    },
                },
            ]);
        }, 600);
    };

    const handleEquipmentTypeSubmit = (equipmentType: string) => {
        wizardDataRef.current.equipmentType = equipmentType;
        setShowEquipmentTypeSelect(false);

        const { serviceLevel, totalWeight, linearFootage, pickupDate } = wizardDataRef.current;

        pushShipmentStep(`a-${Date.now()}-load-details`, [
            {
                title: 'Load Details',
                items: [
                    { label: 'Service Type', value: serviceLevel ? SERVICE_LEVEL_LABELS[serviceLevel] : '' },
                    { label: 'Total Weight', value: `${totalWeight} lbs` },
                    { label: 'Linear ft.', value: `${linearFootage} ft.` },
                    { label: 'Pickup', value: pickupDate ? moment(pickupDate).format('ddd, MMMM Do, YYYY') : '' },
                    { label: 'Equipment', value: EQUIPMENT_TYPE_LABELS[equipmentType] ?? equipmentType },
                ],
            },
        ]);

        // Freeze the Shipment Agent's live timer as done — everything needed to build the
        // shipment has now been collected.
        setMessages((prev) => prev.map((m) => (m.id === shipmentAgentMessageIdRef.current && m.content?.type === 'taskHeader' ? { ...m, content: { ...m.content, status: 'done' } } : m)));

        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: `a-${Date.now()}-review-intro`,
                    sender: 'agent',
                    text: "That's everything I need. Here's your shipment - look it over and confirm.",
                },
                {
                    id: `a-${Date.now()}-review-card`,
                    sender: 'agent',
                    content: {
                        type: 'shipmentReview',
                        fields: {
                            serviceType: serviceTypeRef.current ?? '',
                            items: packagingLineItemsRef.current.length ? formatItemsSummary(packagingLineItemsRef.current) : `${totalWeight} lbs`,
                            origin: 'Concord DC',
                            destination: 'Montreal Cross-Dock',
                            pickup: pickupDate ? moment(pickupDate).format('ddd, Do MMMM, YYYY') : '',
                            equipment: EQUIPMENT_TYPE_LABELS[equipmentType] ?? equipmentType,
                        },
                        onMakeChanges: handleMakeChanges,
                        onCreateShipment: handleCreateShipment,
                    },
                },
            ]);
        }, 800);
    };

    const handleEquipmentTypeBack = () => {
        setShowEquipmentTypeSelect(false);
        setShowPickupDateSelect(true);
    };

    const handleSend = (text: string) => {
        const userMsg: AgentMessage = { id: `u-${Date.now()}`, sender: 'user', text };
        setMessages((prev) => [...prev, userMsg]);

        if (pendingAddressStepRef.current === 'origin') {
            pendingAddressStepRef.current = null;
            setShowOriginAddressSelect(true);

            return;
        }

        if (pendingAddressStepRef.current === 'destination') {
            pendingAddressStepRef.current = null;
            setShowDestinationAddressSelect(true);

            return;
        }

        if (/create\s+(a\s+)?shipment/i.test(text)) {
            runCreateShipmentFlow();

            return;
        }

        if (/track/i.test(text) && /shipment/i.test(text)) {
            setIsLoading(true);

            window.setTimeout(() => {
                setMessages((prev) => [...prev, { id: `a-${Date.now()}`, sender: 'agent', text: 'You can track all your shipments from the Track page in the left sidebar.' }]);
                setIsLoading(false);
            }, 1200);

            return;
        }

        setIsLoading(true);

        // Mock agent reply after 1.5s — offers the two things this demo agent can actually do.
        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                { id: `a-${Date.now()}`, sender: 'agent', text: 'I can help with that! Would you like to create a shipment or track an existing one?' },
                { id: `a-${Date.now()}-option-create`, sender: 'agent', content: { type: 'suggestionChip', label: 'Create a shipment', onClick: () => handleSendRef.current?.('Create a shipment') } },
                { id: `a-${Date.now()}-option-track`, sender: 'agent', content: { type: 'suggestionChip', label: 'Track a shipment', onClick: () => handleSendRef.current?.('Track a shipment') } },
            ]);
            setIsLoading(false);
        }, 1500);
    };

    handleSendRef.current = handleSend;

    const handleServiceTypeSubmit = (type: ShipmentServiceType) => {
        setShowServiceTypeSelect(false);
        serviceTypeRef.current = type;

        // Fill in the value the user just picked — the classified-request card showed
        // "Not Found" until now — and freeze the "Thinking..." timer as done.
        setMessages((prev) =>
            prev.map((m) => {
                if (m.id === thinkingMessageIdRef.current && m.content?.type === 'taskHeader') {
                    return { ...m, content: { ...m.content, status: 'done' } };
                }

                if (m.id === classifyMessageIdRef.current && m.content?.type === 'workflow') {
                    const steps: Step[] = [
                        {
                            ...m.content.steps[0],
                            title: `Classified the request · Shipment · ${type}`,
                            items: [
                                { label: 'Task', value: 'Shipment Creation Request' },
                                { label: 'Type', value: type },
                            ],
                        },
                    ];

                    return { ...m, content: { ...m.content, steps } };
                }

                return m;
            })
        );

        setMessages((prev) => [
            ...prev,
            { id: `a-${Date.now()}-handoff-step`, sender: 'agent', content: { type: 'workflow', steps: [{ title: 'Handed to your Shipment Agent' }] } },
            { id: `a-${Date.now()}-handoff-divider`, sender: 'agent', content: { type: 'divider', text: 'Handed off to Shipment Agent' } },
        ]);

        const shipmentAgentStartedAt = new Date().toISOString();
        const shipmentAgentId = `a-${Date.now()}-shipment-agent`;
        shipmentAgentMessageIdRef.current = shipmentAgentId;

        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: shipmentAgentId,
                    sender: 'agent',
                    content: { type: 'taskHeader', title: 'Shipment Agent', status: 'working', agent: 'rex', startDate: shipmentAgentStartedAt },
                },
            ]);
        }, 600);

        window.setTimeout(() => {
            setMessages((prev) => [
                ...prev,
                {
                    id: `a-${Date.now()}-shipment-agent-ask-origin`,
                    sender: 'agent',
                    text: 'I\'d be happy to help create a shipment. Where is this shipment shipping from? Try a saved location like "Concord DC"',
                },
            ]);
            pendingAddressStepRef.current = 'origin';
        }, 1600);
    };

    const handleClear = () => {
        setMessages([]);
        setIsLoading(false);
        setTaskTitle(null);
        setShowServiceTypeSelect(false);
        setShowOriginAddressSelect(false);
        setShowDestinationAddressSelect(false);
        setShowPackagingDetails(false);
        setShowServiceLevelSelect(false);
        setShowTotalWeightSelect(false);
        setShowPickupDateSelect(false);
        setShowEquipmentTypeSelect(false);
        thinkingMessageIdRef.current = null;
        classifyMessageIdRef.current = null;
        wizardDataRef.current = {};
        pendingAddressStepRef.current = null;
    };

    const lastUserMessage = [...messages].reverse().find((m) => m.sender === 'user');
    const headerTitle = taskTitle ?? (lastUserMessage ? (lastUserMessage.text.length > 40 ? `${lastUserMessage.text.slice(0, 40)}…` : lastUserMessage.text) : 'New Conversation');

    const hasConversation = messages.length > 0;

    const positionStyle: React.CSSProperties = isFullscreen ? { width: 'calc(100% - var(--layout-sidebar-width, 240px))', right: 0 } : { width: `${panelWidth}px`, right: isOpen ? 0 : -panelWidth };

    // Placed after all hooks (rules of hooks). When the feature is disabled for this customer the
    // panel must not even be mounted — a hidden-but-mounted panel is still keyboard-focusable.
    if (!isAgentViewVisible) return null;

    return (
        <div
            className={`${isOpen ? '' : 'pointer-events-none'} absolute z-[22] h-full flex-shrink-0 ${isDragging ? '' : 'transition-[width,right] duration-300 ease-in-out'}`}
            style={positionStyle}
            aria-hidden={!isOpen}
            // `inert` removes the closed panel from the tab order — `pointer-events-none` only blocks
            // the mouse, so without this, tabbing past the last form field on any page focuses the
            // off-screen panel (drag handle has tabIndex=0, composer is an input) and the browser
            // scrolls it into view. React 18 needs the empty-string form; boolean lands in React 19.
            {...(!isOpen ? ({ inert: '' } as any) : {})}
        >
            {/* Drag handle */}
            {!isFullscreen && (
                <div
                    role="slider"
                    tabIndex={0}
                    aria-label="Resize panel"
                    aria-valuemin={MIN_WIDTH}
                    aria-valuemax={MAX_WIDTH}
                    aria-valuenow={panelWidth}
                    className="group absolute left-0 top-0 bottom-0 w-[5px] cursor-col-resize z-10 flex justify-center"
                    onMouseDown={handleMouseDown}
                    onKeyDown={handleResizeKeyDown}
                >
                    <div className="h-full bg-black transition-[width] w-[3px]" />
                </div>
            )}

            <div className={`${isFullscreen ? '' : 'pl-1'} w-full h-full`}>
                <div className="rounded-t-xl border border-utilityGray200 bg-white shadow-xs h-full flex flex-col overflow-hidden">
                    {conversationHistoryOpen && <ConversationHistoryModal handleClose={() => setConversationHistoryOpen(false)} onSelectTask={handleClear} onNewConversation={handleClear} />}

                    {/* Header */}
                    <div className="pt-[9px] pb-2 px-1.5 flex justify-between items-center flex-shrink-0">
                        <div className="flex items-center gap-x-1.5 min-w-0 flex-1 mr-2">
                            <ButtonCmp
                                type="button"
                                className="btn_neutral !text-sm !py-px !px-1.5 truncate"
                                icon={<ChevronDown className="w-3.5 h-3.5 text-grayLight600 flex-shrink-0" />}
                                iconSide="right"
                                onClick={() => setConversationHistoryOpen(true)}
                            >
                                <span className="truncate max-w-[200px]">{headerTitle}</span>
                            </ButtonCmp>
                        </div>

                        <div className="flex items-center gap-x-0.5 flex-shrink-0">
                            <ButtonCmp type="button" className="btn_neutral" size="square-xxs" ariaLabel="Share" icon={<Share06 className="w-4 h-4" />}>
                                <></>
                            </ButtonCmp>
                            <ButtonCmp type="button" className="btn_neutral" size="square-xxs" ariaLabel="New conversation" onClick={handleClear} icon={<Edit05 className="w-4 h-4" />}>
                                <></>
                            </ButtonCmp>
                            <ButtonCmp
                                type="button"
                                className="btn_neutral"
                                size="square-xxs"
                                ariaLabel={isFullscreen ? 'Minimize' : 'Expand'}
                                onClick={() => setIsFullscreen((prev) => !prev)}
                                icon={isFullscreen ? <Minimize01 className="w-4 h-4" /> : <Expand01 className="w-4 h-4" />}
                            >
                                <></>
                            </ButtonCmp>
                            <ButtonCmp type="button" className="btn_neutral" size="square-xxs" ariaLabel="Close" onClick={handleClose} icon={<XClose className="w-4 h-4" />}>
                                <></>
                            </ButtonCmp>
                        </div>
                    </div>

                    {/* Conversation body */}
                    <div ref={scrollContainerRef} className="agent-conversation-box-width flex-1 flex flex-col p-3 !pb-0 overflow-auto scrollbar-hide bg-[inherit] space-y-1.5">
                        {isInitialLoading ? (
                            <ConversationLoading />
                        ) : messages.length === 0 ? (
                            <AgentEmptyState greetingName={currentUser?.firstName} onOpenNewDomain={() => setShowNewDomainModal(true)} />
                        ) : (
                            <>
                                {messages.map((message) => (
                                    <AgentMessageBubble key={message.id} message={message} />
                                ))}
                                {isLoading && <AiThinking />}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="agent-conversation-box-width p-3 !pt-5 space-y-3 flex-shrink-0">
                        {hasConversation && !isInitialLoading && !showServiceTypeSelect && !taskTitle && <AllRatesLists items={MOCK_RATES} />}
                        {showServiceTypeSelect && <ServiceTypeSelectionCard onSubmit={handleServiceTypeSubmit} onClose={() => setShowServiceTypeSelect(false)} />}
                        {showOriginAddressSelect && (
                            <AddressSelectionCard
                                title="Select origin from your saved locations"
                                savedAddresses={ORIGIN_SAVED_ADDRESSES}
                                onSubmit={handleOriginAddressSubmit}
                                onClose={() => setShowOriginAddressSelect(false)}
                            />
                        )}
                        {showDestinationAddressSelect && (
                            <AddressSelectionCard
                                title="Select destination from your saved locations"
                                savedAddresses={DESTINATION_SAVED_ADDRESSES}
                                onSubmit={handleDestinationAddressSubmit}
                                onClose={() => setShowDestinationAddressSelect(false)}
                            />
                        )}
                        {showPackagingDetails && <PackagingDetailsCard onConfirm={handlePackagingConfirm} onBack={handlePackagingBack} onClose={() => setShowPackagingDetails(false)} />}
                        {showServiceLevelSelect && (
                            <ServiceLevelSelectionCard step={1} totalSteps={4} onNext={handleServiceLevelNext} onBack={handleServiceLevelBack} onClose={() => setShowServiceLevelSelect(false)} />
                        )}
                        {showTotalWeightSelect && (
                            <TotalWeightCard step={2} totalSteps={4} onNext={handleTotalWeightNext} onBack={handleTotalWeightBack} onClose={() => setShowTotalWeightSelect(false)} />
                        )}
                        {showPickupDateSelect && <PickupDateCard step={3} totalSteps={4} onNext={handlePickupDateNext} onBack={handlePickupDateBack} onClose={() => setShowPickupDateSelect(false)} />}
                        {showEquipmentTypeSelect && (
                            <EquipmentTypeCard step={4} totalSteps={4} onSubmit={handleEquipmentTypeSubmit} onBack={handleEquipmentTypeBack} onClose={() => setShowEquipmentTypeSelect(false)} />
                        )}
                        <AgentComposer onSend={handleSend} disabled={isLoading} />
                    </div>
                </div>
            </div>
            {showNewDomainModal && <NewDomain handleClose={() => setShowNewDomainModal(false)} />}
        </div>
    );
};

export default AgentConversationPanel;
